const LEVEL_LABELS = {
  INTRODUCTORY: 'Introductory',
  INTERMEDIATE: 'Intermediate',
  PROFESSIONAL: 'Professional',
} as const;

/**
 * The generated client types these columns as non-null, but they are nullable
 * in the database and null across the seeded catalogue, so each formatter takes
 * the widened type (sdk-it #684).
 */
export function formatLevel(level: keyof typeof LEVEL_LABELS) {
  return LEVEL_LABELS[level] ?? level;
}

/** Null when no hours are set; the caller hides the row rather than filling it. */
export function formatDuration(durationHours: number | null) {
  return durationHours ? `${durationHours} Hours` : null;
}

const dayFormatter = new Intl.DateTimeFormat('en', {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
});

export function formatStartDate(startDate: string | null) {
  if (!startDate) return 'Coming Soon';
  return dayFormatter.format(new Date(startDate));
}

/**
 * A cohort with no end date still reads as a single day, so the range collapses
 * rather than trailing an em dash into nothing.
 */
export function formatDateRange(
  startDate: string | null,
  endDate: string | null,
) {
  if (!startDate) return 'Coming Soon';
  if (!endDate) return dayFormatter.format(new Date(startDate));

  return dayFormatter.formatRange(new Date(startDate), new Date(endDate));
}

const WWW_APP_URL = import.meta.env.VITE_WWW_APP_URL ?? '';

/**
 * Catalogue images authored before uploads existed are static files that ship
 * with `apps/www` only, so a bare `/images/...` path 404s on this origin. They
 * are resolved against the public site rather than duplicated into this app's
 * bundle. Uploaded images (`/files/:id/raw`) stay relative — every hosting site
 * rewrites that prefix to the API.
 */
export function courseImage(imageUrl: string | null) {
  if (!imageUrl) return undefined;
  if (imageUrl.startsWith('/images/')) return `${WWW_APP_URL}${imageUrl}`;

  return imageUrl;
}

/**
 * Prices are stored in halalas so the amount charged can be compared to the
 * amount published without any rounding in between. Only display divides.
 */
export function formatPrice(price: number | null) {
  if (price === null || price === undefined) return 'TBD';

  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency: 'SAR',
  }).format(price / 100);
}
